import {Duplexer, InstanceUpdater, VirtualSocket} from '@wix/duplexer-js'
import {CoreLogger, debug} from '@wix/document-manager-core'
import {EventEmitter} from 'events'
import {editorX} from '@wix/document-manager-utils'
import type {Experiment} from '@wix/document-services-types'
const log = debug('channel')

export const editorXUri = 'duplexer.editorx.com'
export const wixUri = 'duplexer.wix.com'

export const appDefId = '251a12da-ff5c-4f20-a7b0-4fc8a9eb3cf4'
const DEFAULT_BRANCH_ID = '00000000-0000-0000-0000-000000000000'

export enum ChannelEvents {
    approved = 'approved',
    rejected = 'rejected',
    batchTransactions = 'batchTransactions',
    majorSiteChange = 'majorSiteChange',
    outOfSync = 'outOfSync',
    viewsUpdate = 'viewsUpdate',
    messageDistribute = 'messageDistribute'
}

export enum DuplexerEvents {
    connected = '@duplexer:connected',
    disconnected = '@duplexer:disconnected',
    connectError = '@duplexer:connect_error',
    subscriptionSucceeded = '@duplexer:subscription_succeeded',
    subscriptionFailed = '@duplexer:subscription_failed'
    // unsubscribe_succeeded = '@duplexer:unsubscribe_succeeded'
}

export interface EventChannel {
    on(msg: string, cb: Function): void
    emit(msg: string, ...args: any[]): void
}

export interface AppConnection extends EventChannel {
    subscribe(channelName: string, opts?: any): Promise<EventChannel>
}

export interface Channel {
    readonly connected: boolean
    on(msg: string, cb: Function): void
    removeChannelEventListeners(): void
    rejectNext(): void
    subscribe(channelName: string, experimentInstance: Experiment): Promise<EventChannel>
    simulateApproval(correlationId: string, transactionId: string): Promise<void>
    // testing
    simulateSubscriptionSucceeded(args: SubscriptionSucceededArgs): void
    close(): void
}

interface SubscriptionSucceededArgs {
    isSynced: boolean
}

export type EventCB = (...args: any[]) => void

export type WixInstanceProvider = InstanceUpdater['getInstance']

const getUri = (origin: string) => {
    if (origin === editorX) {
        return editorXUri
    }
    return wixUri
}

export class DuplexerChannel implements Channel {
    private duplexer: Duplexer
    public readonly emitter: EventEmitter = new EventEmitter()
    protected _channel: EventChannel | null = null
    get channel(): EventChannel | null {
        return this._channel
    }
    private _connected: boolean = false
    protected appConnection: VirtualSocket | null = null
    get connected(): boolean {
        return this._connected
    }
    private logger?: CoreLogger

    constructor(
        wixInstanceProvider: WixInstanceProvider,
        origin: string,
        logger?: CoreLogger,
        private branch?: string,
        dup?: Duplexer
    ) {
        const instanceUpdater = {getInstance: wixInstanceProvider}
        if (dup) {
            this.duplexer = dup
        } else {
            const uri = getUri(origin)
            this.duplexer = new Duplexer(uri, {instanceUpdater})
        }
        this.logger = logger
    }

    connect(): Promise<void> {
        this.appConnection = this.duplexer.connect({appDefId})
        return new Promise<void>((resolve, reject) => {
            this.appConnection!.on(DuplexerEvents.connected, () => {
                this._connected = true
                this.logger?.interactionStarted('DuplexerConnected')
                log.info('Duplexer successfully connected')
                resolve()
            })
            this.appConnection!.on(DuplexerEvents.connectError, (error?: Error) => {
                console.log('Error connecting to Duplexer!')
                this._connected = false
                this.logger?.interactionStarted('DuplexerConnectError', {extras: {error: JSON.stringify(error)}})
                reject(new Error('fail connecting to Duplexer'))
            })
            this.appConnection!.on(DuplexerEvents.disconnected, (error?: Error) => {
                console.log('Duplexer disconnected!')
                this._connected = false
                this.logger?.interactionStarted('DuplexerDisconnected', {extras: {error: JSON.stringify(error)}})
            })
        })
    }

    on(msg: string, cb: EventCB) {
        this.emitter.on(msg, cb)
    }

    removeChannelEventListeners() {
        this.emitter.removeAllListeners()
    }

    async subscribe(channelName: string, experimentInstance: Experiment) {
        await this.connect()
        const resourceId =
            (this.branch && this.branch !== DEFAULT_BRANCH_ID && experimentInstance.isOpen('dm_duplexerResourceId')) ||
            (this.branch && !experimentInstance.isOpen('dm_duplexerResourceId'))
                ? this.branch
                : null
        this._channel = await this.appConnection!.subscribe(channelName, resourceId ? {resourceId} : undefined)
        this._channel.on(DuplexerEvents.subscriptionSucceeded, (args: SubscriptionSucceededArgs) => {
            /**
             * if the duplexer is synced, then all missed event will be sent via the channel
             * otherwise we will load it from the document store
             */
            this.logger?.interactionStarted('DuplexerSubscriptionSucceeded', {
                extras: {
                    isSynced: args.isSynced,
                    channelName,
                    branchId: this.branch,
                    resourceId
                }
            })
            if (!args.isSynced) {
                this.emitter.emit(ChannelEvents.outOfSync)
            }
            log.info('Successfully subscribed to channel isSynced =', args.isSynced)
        })
        this._channel.on(DuplexerEvents.subscriptionFailed, () => {
            console.log('Error subscribing to channel!')
        })
        const events = [
            ChannelEvents.approved,
            ChannelEvents.rejected,
            ChannelEvents.majorSiteChange,
            ChannelEvents.viewsUpdate,
            ChannelEvents.messageDistribute,
            ChannelEvents.batchTransactions
        ]
        events.forEach(event => {
            this._channel!.on(event, (...args: any[]) => {
                this.emitter.emit(event, ...args)
            })
        })
        return this._channel
    }

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    async simulateApproval(correlationId: string, transactionId: string) {}

    simulateSubscriptionSucceeded(args: SubscriptionSucceededArgs) {
        this._channel?.emit(DuplexerEvents.subscriptionSucceeded, args)
    }

    rejectNext() {}

    close() {
        this.duplexer.close()
    }
}

export interface DuplexerSource {
    createDuplexer(
        wixInstanceProvider: WixInstanceProvider,
        origin: string,
        logger: CoreLogger,
        branch?: string
    ): Channel
}

export const createDuplexer = (
    wixInstanceProvider: WixInstanceProvider,
    origin: string,
    logger: CoreLogger,
    branch?: string
): Channel => new DuplexerChannel(wixInstanceProvider, origin, logger, branch)
